import { useState } from "react";
import { Send, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useCollection } from "@/hooks/useCollection";
import { PageHeader, Panel, StatusBadge, Empty } from "@/components/crm/common";
import { FlowStepper } from "@/components/crm/FlowStepper";
import { ProjectSelect } from "@/components/crm/ProjectSelect";
import { FormDialog } from "@/components/crm/FormDialog";
import { FileBox } from "@/components/crm/Files";
import { useAuth } from "@/context/AuthContext";

const STEPS = ["Brief", "Concept", "Design", "Costing", "Approval"];
const CHECKS = [
  ["brief_reviewed", "Client brief reviewed"], ["info_clarified", "Missing information clarified"], ["concept_assigned", "Concept and theme assigned"],
  ["design_briefed", "Design Concept team briefed"], ["element_sheet", "Event element sheet prepared"], ["costing_reviewed", "Costing and execution feasibility reviewed"],
  ["internal_approval", "Internal presentation approval completed"],
];
const BRIEF_FIELDS = [
  { name: "objective", label: "Event objective", full: true },
  { name: "deliverables", label: "Required deliverables", type: "textarea" },
  { name: "brief", label: "Client brief / reference links", type: "textarea" },
  { name: "moodboard", label: "Moodboard link" },
  { name: "client_deadline", label: "Submission deadline", type: "date" },
];

export default function Pitch() {
  const { items: projects, update } = useCollection("projects");
  const { create: createApproval } = useCollection("approvals", null);
  const { can } = useAuth();
  const canEdit = can("Pitch", "edit");
  const [pid, setPid] = useState("");
  const [edit, setEdit] = useState(false);
  const p = projects.find((x) => x.id === pid);
  const pitchProjects = projects.filter((x) => ["New Brief", "Pitch", "Approval"].includes(x.stage));

  const toggle = (k) => update(p.id, { pitch_checklist: { ...(p.pitch_checklist || {}), [k]: !p.pitch_checklist?.[k] } }, null);
  const submitReview = async () => {
    await createApproval({ project_id: p.id, project_name: p.name, title: `${p.name} — Pitch presentation`, type: "Presentation", submitted_by: p.cs_spoc, status: "Pending", route_step: 0, notes: "" }, null);
    await update(p.id, { stage: "Approval", pitch_step: 4 }, "Submitted for internal review → Approvals");
  };
  const done = p ? CHECKS.filter(([k]) => p.pitch_checklist?.[k]).length : 0;

  return (
    <>
      <PageHeader num="03" title="Pitch Workspace" subtitle="Move from client brief to an internally reviewed pitch.">
        <ProjectSelect projects={projects} stages={["New Brief", "Pitch", "Approval"]} value={pid} onChange={setPid} />
      </PageHeader>
      {!pitchProjects.length && <Panel><Empty text="No projects in New Brief / Pitch stage. Create one in Project Management." /></Panel>}
      {p && (
        <>
          <Panel>
            <FlowStepper steps={STEPS} current={p.pitch_step || 0} testid="pitch-stepper" onSelect={!canEdit ? undefined : (i) => update(p.id, { pitch_step: i, stage: p.stage === "New Brief" && i > 0 ? "Pitch" : p.stage }, `Pitch step: ${STEPS[i]}`)} />
          </Panel>
          <div className="grid lg:grid-cols-5 gap-6">
            <Panel className="lg:col-span-3" title="Brief information" action={canEdit && <Button data-testid="pitch-edit-brief-button" size="sm" variant="outline" onClick={() => setEdit(true)}><Pencil className="h-3.5 w-3.5 mr-1" />Edit brief</Button>}>
              <dl className="space-y-4 text-sm">
                {[["Project name", `${p.name} — ${p.client}`], ["Stage", <StatusBadge key="s" value={p.stage} />], ["Event objective", p.objective], ["Required deliverables", p.deliverables],
                  ["Client brief", p.brief], ["Moodboard", p.moodboard], ["Submission deadline", p.client_deadline], ["CS SPOC (client coordination)", p.cs_spoc]].map(([k, v]) => (
                  <div key={k}><dt className="text-[11px] uppercase tracking-wider text-slate-400">{k}</dt><dd className="text-slate-800 mt-0.5">{v || "—"}</dd></div>
                ))}
              </dl>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <FileBox testid="pitch-brief-files" entityType="project" entityId={p.id} category="brief" title="Client brief files" canUpload={canEdit} />
                <FileBox testid="pitch-moodboard-files" entityType="project" entityId={p.id} category="moodboard" title="Moodboards & concepts" canUpload={canEdit} />
              </div>
            </Panel>
            <Panel className="lg:col-span-2" title={`Pitch checklist · ${done}/${CHECKS.length}`}>
              <div className="space-y-3">
                {CHECKS.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-3 text-sm text-slate-700 cursor-pointer">
                    <Checkbox data-testid={`pitch-check-${k}`} disabled={!canEdit} checked={!!p.pitch_checklist?.[k]} onCheckedChange={() => toggle(k)} />
                    <span className={p.pitch_checklist?.[k] ? "line-through text-slate-400" : ""}>{label}</span>
                  </label>
                ))}
              </div>
              <Button data-testid="pitch-submit-review-button" disabled={p.stage === "Approval" || !canEdit} onClick={submitReview} className="w-full mt-6">
                <Send className="h-4 w-4 mr-1" />{p.stage === "Approval" ? "Submitted — in Approvals" : "Submit for Internal Review"}
              </Button>
            </Panel>
          </div>
          <p className="text-xs text-slate-500">CS owns client information and coordination; concept, design, execution and production teams manage their assigned contributions via Work Assignment.</p>
          <FormDialog testid="brief-form" open={edit} onOpenChange={setEdit} title="Edit brief" fields={BRIEF_FIELDS} initial={p} onSubmit={async (f) => !!(await update(p.id, f, "Brief saved"))} />
        </>
      )}
    </>
  );
}
